import { useState, useEffect, useContext, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Avatar,
  Popover,
} from '@mui/material'
import * as Icons from '@mui/icons-material'
import logo from '../assets/appbar-logo.png'
import wordmark from '../assets/appbar-wordmark.png'
import { supabaseGetter } from '~/api/supabase-getter'
import useMediaQuery from '@mui/material/useMediaQuery'
import theme from '../styles/mui-theme'
import { BRAND_PRIMARY } from '~/styles/palette'
import { useAuthContext } from '../common/context/auth/auth-context'
import { ThemeContext } from '../common/context/theme/theme-context'
import { FarmSelector } from './farm-selector'
import { useCurrentUser } from '../common/use-current-user'
import { useFeatures } from '../common/use-features'
import { useUserFarms } from '../common/use-user-farms'
import { useFarmId } from '../common/use-farm-id'
import { CacheDebugPanel } from '~/api/cache-debug-panel'
import { supabase } from '~/supabase-client'
import { APPBAR_EXTRA_DESKTOP_REM, APPBAR_HEIGHT_REM, DESKTOP_BREAKPOINT } from '~/common/constants/layout-constants'
import { InviteUserModal } from '~/invite-user/invite-user-modal'
import { EndSeasonModal } from '~/end-season/end-season-modal'
import { ExportTimelineModal } from '~/crop/crop-schedule/crop-timeline/export/export-timeline-modal'
import { SeasonSelector } from '~/common/components/season-selector'
import { cacheManager } from '~/api/cache-manager'
import { FARM_SELECTOR_HIDDEN_ROUTES } from '~/common/constants/route-constants'

function AppBarComponent({ onMenuClick }) {
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null)
  const [userMenuWrapperEl, setUserMenuWrapperEl] = useState(null)
  const [profile, setProfile] = useState({ username: '', avatar_url: '' })
  const [avatarUrl, setAvatarUrl] = useState(null)
  const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false)
  const [isEndSeasonOpen, setIsEndSeasonOpen] = useState(false)
  const [isExportTimelineOpen, setIsExportTimelineOpen] = useState(false)
  const [farmSelectorAnchor, setFarmSelectorAnchor] = useState(null)
  const farmSelectorRef = useRef(null)
  
  const navigate = useNavigate()
  const userMenuOpen = Boolean(userMenuAnchorEl)
  const { user: authUser, session, signOut } = useAuthContext()
  const { currentUser } = useCurrentUser()
  const { features } = useFeatures()
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT))
  const { userFarms } = useUserFarms({ user: currentUser })
  const { farmId } = useFarmId()
  const farm = userFarms?.find(f => f.id === farmId)
  const farmName = farm?.name
  const { mode, setMode } = useContext(ThemeContext)
  const isSuperAdmin = currentUser?.permissions === 1

  const handleThemeCycle = () => {
    const modes = ['light', 'dark', 'system']
    const currentIndex = modes.indexOf(mode)
    const nextIndex = (currentIndex + 1) % modes.length
    setMode(modes[nextIndex])
  }
  
  useEffect(() => {
    async function getProfile() {
      if (!session?.user) return

      try {
        const { data, error } = await supabaseGetter.users.getById(session.user.id)

        if (error) throw error
        if (data) {
          setProfile({
            username: data.username,
            avatar_url: data.avatar_url
          })
        }
      } catch (error) {
        console.warn('Error loading profile:', error)
      }
    }

    getProfile()

    // Listen for profile update events
    const handleProfileUpdate = () => {
      getProfile()
    }

    window.addEventListener('profile-updated', handleProfileUpdate)

    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdate)
    }
  }, [session])

  useEffect(() => {
    async function downloadAvatar(path) {
      try {
        const { data, error } = await supabase.storage.from('avatars').download(path)
        if (error) throw error
        const url = URL.createObjectURL(data)
        setAvatarUrl(url)
      } catch (error) {
        console.log('Error downloading avatar:', error.message)
        setAvatarUrl(null)
      }
    }

    if (profile.avatar_url && profile.avatar_url.trim() !== '') {
      downloadAvatar(profile.avatar_url)
    } else {
      setAvatarUrl(null)
    }
  }, [profile.avatar_url])

  const handleHome = () => {
    window.open('/', '_blank')
  }

  const handleMenuOpen = () => {
    if (!authUser) return
    setUserMenuAnchorEl(userMenuWrapperEl)
  }

  const handleMenuClose = () => {
    setUserMenuAnchorEl(null)
  }

  const handleNav = (route) => {
    handleMenuClose()
    navigate(route)
  }

  const handleInvalidateCache = async () => {
    cacheManager.invalidateAll()
  }

  const handleLogout = () => {
    handleMenuClose()
    signOut()
  }

  const handleShowInviteUserModal = () => {
    setIsInviteUserModalOpen(true)
  }

  return (
    <AppBar
      position="fixed"
      color="primary"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        '& .MuiToolbar-gutters': {
          p: '0 0.5rem',
        },
        height: isDesktop ? APPBAR_HEIGHT_REM : `calc(${APPBAR_HEIGHT_REM} - ${APPBAR_EXTRA_DESKTOP_REM})`,
        backgroundColor: BRAND_PRIMARY, // Always use primary brand color regardless of theme mode
      }}
    >
      <Toolbar sx={{ height: '100%', minHeight: 'unset !important' }}>
        {isDesktop ? (
          <Box
            sx={{
              p: 0.5,
              mr: 2,
              borderRadius: 1,
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
            onClick={handleHome}
          >
            <Box component="img" src={wordmark} alt="TerraPlanner" sx={{ height: 50, display: 'block', borderRadius: 0.75 }} />
          </Box>
        ) : (
          <>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onMenuClick}
              sx={{ ml: 0 }}
            >
              <Icons.Menu />
            </IconButton>

            <Box
              sx={{
                p: 0.5,
                mr: 2,
                borderRadius: 1,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
              onClick={handleHome}
            >
              <Box component="img" src={logo} alt="TerraPlanner" sx={{ height: 40, display: 'block' }} />
            </Box>
          </>
        )}

        {/* Show farm selector for dev admins or if any user has > 1 farm (appbar desktop only) */}
        {/* {
          isSuperAdmin && isDesktop && (
            (features['add_farm'] && userFarms?.length > 1) ||
            // Don't show the selector certain routes
            !FARM_SELECTOR_HIDDEN_ROUTES.includes(location?.pathname)
          ) && <FarmSelector />
        } */}

        {/* cache debugger popup */}
        {isSuperAdmin && features['cache_debug'] && <CacheDebugPanel />}

        <Box sx={{ flexGrow: 1 }} />

        {/* Farm selector - mobile icon button */}
        {isSuperAdmin && !isDesktop && (
          userFarms?.length > 1 ||
          !FARM_SELECTOR_HIDDEN_ROUTES.includes(location?.pathname)
        ) && (
          <>
            <Box
              ref={farmSelectorRef}
              sx={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch' }}
              onClick={() => setFarmSelectorAnchor(farmSelectorRef.current)}
            >
              <IconButton color="inherit" aria-label="select farm" sx={{ mr: 0.5 }}>
                <Icons.Gite />
              </IconButton>
            </Box>
            <Popover
              open={Boolean(farmSelectorAnchor)}
              anchorEl={farmSelectorAnchor}
              onClose={() => setFarmSelectorAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              marginThreshold={0}
              slotProps={{ paper: { sx: { minWidth: 240, pr: 1 } } }}
            >
              <Box sx={{ p: 1.5 }}>
                <FarmSelector lightBackground usePortal onSelect={() => setFarmSelectorAnchor(null)} />
              </Box>
            </Popover>
          </>
        )}

        <SeasonSelector />

        <Box
          ref={setUserMenuWrapperEl}
          sx={{
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'stretch',
            gap: 1,
            px: 1,
            cursor: 'pointer',
            position: 'relative',
            transition: 'background-color 0.2s',
            
            '&:hover::before': {
              content: '""',
              position: 'absolute',
              top: 8,
              bottom: 8,
              left: 0,
              right: 0,
              borderRadius: 1,
              border: '4px solid rgba(255,255,255,0.1)',
              pointerEvents: 'none',
            },
          }}
          onClick={handleMenuOpen}
          aria-label="user menu"
          aria-controls={userMenuOpen ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={userMenuOpen ? 'true' : undefined}
        >
          {profile.username && (
            <Typography
              variant="body2"
              sx={{
                color: 'inherit',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {profile.username}
            </Typography>
          )}
          {avatarUrl ? (
            <Avatar
              src={avatarUrl}
              alt={profile.username || 'User'}
              sx={{ width: 32, height: 32 }}
            />
          ) : (
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
              {profile.username ? (
                <Typography variant='body2' color='#fff'>
                  {profile.username.charAt(0).toUpperCase()}
                </Typography> 
              ) : <Icons.AccountCircle />}
            </Avatar>
          )}
        </Box>
        <Menu
          id="user-menu"
          anchorEl={userMenuAnchorEl}
          open={userMenuOpen}
          onClose={handleMenuClose}
          slotProps={{ paper: { sx: { minWidth: 240 } } }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          marginThreshold={0}
        >
          {features['theme_toggle'] && (
            <MenuItem onClick={handleThemeCycle}>
              <ListItemIcon>
                {mode === 'light' && <Icons.LightMode fontSize="small" />}
                {mode === 'dark' && <Icons.DarkMode fontSize="small" />}
                {mode === 'system' && <Icons.SettingsBrightness fontSize="small" />}
              </ListItemIcon>
              <ListItemText>
                {mode === 'light' ? 'Light' : mode === 'dark' ? 'Dark' : 'System'} Theme
              </ListItemText>
            </MenuItem>
          )}

          <MenuItem onClick={() => handleNav('/account')}>
            <ListItemIcon>
              <Icons.Person fontSize="small" />
            </ListItemIcon>
            <ListItemText>Account</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => handleNav(farmId ? `/preferences?farmId=${farmId}` : '/preferences')}>
            <ListItemIcon>
              <Icons.Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText>Preferences</ListItemText>
          </MenuItem>

          {features['invite_users'] && (
            <MenuItem onClick={() => handleShowInviteUserModal(true)}>
              <ListItemIcon>
                <Icons.GroupAdd fontSize="small" />
              </ListItemIcon>
              <ListItemText>Invite Users</ListItemText>
            </MenuItem>
          )}

          {features['export_timeline'] && (
            <MenuItem onClick={() => { handleMenuClose(); setIsExportTimelineOpen(true) }}>
              <ListItemIcon>
                <Icons.PictureAsPdf fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export Timeline</ListItemText>
            </MenuItem>
          )}

          {features['end_season'] && (
            <MenuItem onClick={() => { handleMenuClose(); setIsEndSeasonOpen(true) }}>
              <ListItemIcon>
                <Icons.PublishedWithChanges fontSize="small" sx={{ color: theme.palette.warning.main }} />
              </ListItemIcon>
              <ListItemText sx={{ color: theme.palette.warning.main }}>Complete Season</ListItemText>
            </MenuItem>
          )}

          <Divider />

          {currentUser?.permissions === 1 && (
            <MenuItem onClick={handleInvalidateCache}>
              <ListItemIcon>
                <Icons.Refresh fontSize="small" sx={{ color: theme.palette.error[400] }} />
              </ListItemIcon>
              <ListItemText sx={{ color: theme.palette.error[400] }}>Invalidate All</ListItemText>
            </MenuItem>
          )}

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Icons.Logout fontSize="small" sx={{ color: theme.palette.error.main }} />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.error.main }}>Logout</ListItemText>
          </MenuItem>
        </Menu>

        <InviteUserModal open={isInviteUserModalOpen} onClose={() => setIsInviteUserModalOpen(false)} />
        <EndSeasonModal open={isEndSeasonOpen} onClose={() => setIsEndSeasonOpen(false)} />
        <ExportTimelineModal open={isExportTimelineOpen} onClose={() => setIsExportTimelineOpen(false)} farmName={farmName} farmLat={farm?.lat} farmLng={farm?.lng} />
      </Toolbar>
    </AppBar>
  )
}

export default AppBarComponent
